import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

const emptyLine = () => ({ productId: '', quantity: '1', estimatedCost: '0' });

export function RequisitionCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [warehouseId, setWarehouseId] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [justification, setJustification] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [error, setError] = useState(null);

  const warehouses = useQuery({ queryKey: ['inventory', 'warehouses'], queryFn: () => inventoryApi.listWarehouses().then((r) => r.items), enabled: open });
  const products = useQuery({ queryKey: ['inventory', 'products', 'all'], queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items), enabled: open });

  useEffect(() => {
    if (open) {
      setWarehouseId(''); setNeededBy(''); setJustification(''); setLines([emptyLine()]); setError(null);
    }
  }, [open]);

  const setLine = (idx, patch) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  // Pre-fill estimated unit cost from the product's list price when picked.
  function pickProduct(idx, productId) {
    const p = (products.data || []).find((x) => x.id === productId);
    setLine(idx, { productId, estimatedCost: p ? String(p.listPrice ?? '0') : '0' });
  }

  const save = useMutation({
    mutationFn: (body) => inventoryApi.createRequisition(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['inventory', 'requisitions'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    const valid = lines.filter((l) => l.productId && Number(l.quantity) > 0);
    if (valid.length === 0) { setError(t('req.error.lines')); return; }
    save.mutate({
      warehouseId: warehouseId || null,
      neededBy: neededBy || null,
      justification: justification || null,
      lines: valid.map((l) => ({ productId: l.productId, quantity: Number(l.quantity), estimatedCost: Number(l.estimatedCost || 0) })),
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('req.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? `${t('common.save')}…` : t('common.save')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('req.field.warehouse')}</span>
            <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
              <option value="">-</option>
              {(warehouses.data || []).map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('req.field.neededBy')}</span>
            <input className="input" type="date" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('req.field.justification')}</span>
          <input className="input" value={justification} onChange={(e) => setJustification(e.target.value)} />
        </label>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t('req.field.product')}</th>
                <th className="num">{t('req.field.quantity')}</th>
                <th className="num">{t('req.field.estCost')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => (
                <tr key={idx}>
                  <td>
                    <select className="input" value={l.productId} onChange={(e) => pickProduct(idx, e.target.value)}>
                      <option value="">-</option>
                      {(products.data || []).map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
                    </select>
                  </td>
                  <td className="num"><input className="input num" type="number" min="0" step="1" value={l.quantity} onChange={(e) => setLine(idx, { quantity: e.target.value })} /></td>
                  <td className="num"><input className="input num" type="number" min="0" step="0.01" value={l.estimatedCost} onChange={(e) => setLine(idx, { estimatedCost: e.target.value })} /></td>
                  <td><Button size="sm" variant="ghost" type="button" onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls))}>×</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button size="sm" variant="ghost" type="button" onClick={() => setLines((ls) => [...ls, emptyLine()])}>{t('req.action.addLine')}</Button>

        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
